import React, { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calculator } from "lucide-react";
import { formatPrice } from "@/lib/api";

export default function EmiCalculator({ defaultAmount = 0 }) {
  const [amount, setAmount] = useState(defaultAmount ? String(defaultAmount) : "");
  const [rate, setRate] = useState("8.5");
  const [years, setYears] = useState("20");

  const { emi, totalInterest, totalPayable } = useMemo(() => {
    const P = parseFloat(amount) || 0;
    const r = (parseFloat(rate) || 0) / 12 / 100;
    const n = (parseFloat(years) || 0) * 12;
    if (!P || !r || !n) return { emi: 0, totalInterest: 0, totalPayable: 0 };
    const e = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    const total = e * n;
    return { emi: e, totalInterest: total - P, totalPayable: total };
  }, [amount, rate, years]);

  return (
    <div className="bg-card border border-brand-line rounded-2xl p-6" data-testid="emi-calculator">
      <h3 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2"><Calculator className="w-5 h-5 text-brand-emerald" /> EMI Calculator</h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div><Label className="text-xs">Loan Amount (₹)</Label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1" data-testid="emi-amount" /></div>
        <div><Label className="text-xs">Interest Rate (% p.a.)</Label><Input type="number" value={rate} onChange={(e) => setRate(e.target.value)} className="mt-1" data-testid="emi-rate" /></div>
        <div><Label className="text-xs">Tenure (years)</Label><Input type="number" value={years} onChange={(e) => setYears(e.target.value)} className="mt-1" data-testid="emi-years" /></div>
      </div>
      <div className="grid grid-cols-3 gap-3 mt-5 text-center">
        <div className="bg-brand-emerald/10 rounded-xl p-3">
          <div className="text-xs text-brand-muted">Monthly EMI</div>
          <div className="font-heading font-bold text-brand-emerald text-lg" data-testid="emi-result">{emi ? formatPrice(Math.round(emi)) : "—"}</div>
        </div>
        <div className="bg-secondary rounded-xl p-3">
          <div className="text-xs text-brand-muted">Total Interest</div>
          <div className="font-heading font-semibold text-brand-ink">{totalInterest ? formatPrice(Math.round(totalInterest)) : "—"}</div>
        </div>
        <div className="bg-secondary rounded-xl p-3">
          <div className="text-xs text-brand-muted">Total Payable</div>
          <div className="font-heading font-semibold text-brand-ink">{totalPayable ? formatPrice(Math.round(totalPayable)) : "—"}</div>
        </div>
      </div>
    </div>
  );
}
